import React from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';

import SectionAccordion from './SectionAccordion';
import { menuFormTheme, textInputCss } from '../utils/form-styles';
import { TEXT_SECONDARY_COLOR, THEME_COLOR5 } from '../../../../../constants';
import type { TMenuItemFormValues } from '../schemas/menu-item-schema';

type TPricingSectionProps = {
  accordionRef: React.RefObject<HTMLElement>;
  currencySymbol: string;
  expanded?: boolean;
  iconColor: string;
  headerBackground: string;
  onToggle?: (expanded: boolean) => void;
};

const PricingSection = ({ accordionRef, currencySymbol, expanded, onToggle, iconColor, headerBackground }: TPricingSectionProps) => {
  const {
    register,
    formState: { errors, isSubmitted },
  } = useFormContext<TMenuItemFormValues>();

  const hasError = isSubmitted && !!(errors.priceValue || errors.takeawayPriceValue);

  return (
    <SectionAccordion
      accordionRef={accordionRef}
      title="Pricing"
      subtitle="Dine-in & takeaway prices"
      icon={<PaidOutlinedIcon fontSize="small" />}
      iconColor={iconColor}
      headerBackground={headerBackground}
      hasError={hasError}
      expanded={expanded}
      onToggle={onToggle}
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Typography style={labelStyle}>Dine-in Price *</Typography>
          <ThemeProvider theme={menuFormTheme}>
            <TextField
              required
              focused
              fullWidth
              id="priceValue"
              type="number"
              variant="standard"
              sx={textInputCss}
              error={Boolean(errors.priceValue)}
              helperText={errors.priceValue?.message}
              InputProps={{
                autoComplete: 'none',
                sx: { fontSize: 13, fontFamily: 'GilroyBold' },
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={s.currency}>{currencySymbol}</Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ step: '0.01', min: '0' }}
              {...register('priceValue', { valueAsNumber: true })}
            />
          </ThemeProvider>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography style={labelStyle}>Takeaway Price</Typography>
          <ThemeProvider theme={menuFormTheme}>
            <TextField
              focused
              fullWidth
              id="takeawayPriceValue"
              type="number"
              variant="standard"
              sx={textInputCss}
              error={Boolean(errors.takeawayPriceValue)}
              helperText={errors.takeawayPriceValue?.message}
              InputProps={{
                autoComplete: 'none',
                sx: { fontSize: 13, fontFamily: 'GilroyBold' },
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={s.currency}>{currencySymbol}</Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ step: '0.01', min: '0' }}
              {...register('takeawayPriceValue', { valueAsNumber: true })}
            />
          </ThemeProvider>
          <Typography sx={s.hint}>Leave blank to use the dine-in price.</Typography>
        </Grid>
      </Grid>
    </SectionAccordion>
  );
};

export default PricingSection;

const labelStyle = { color: THEME_COLOR5, fontFamily: 'GilroyRegular', fontSize: 12 } as React.CSSProperties;

const s = {
  currency: { fontSize: 13, fontFamily: 'GilroyBold', color: '#000' },
  hint: { mt: 1, fontFamily: 'GilroyRegular', fontSize: 12, color: TEXT_SECONDARY_COLOR },
};
